// Libraries
import { SmallCloseIcon } from '@chakra-ui/icons';
import { Tag, TagRightIcon } from '@chakra-ui/react';
import { useCallback } from 'react';
// Utils
import { replaceElementInArray } from '@helpers/index';
import text from '@translations/en.json';
// Types
import { useUnitAndRangeStore } from '@stores/index';
import { PillProps } from './Pill.types';

/**
 * Renders a pill with a close button to remove the filter when clicked.
 *
 * @component
 * @param {PillProps} PillProps - The props for the Pill component.
 * @param {string} filter - The filter to display in the pill.
 * @param {string} filterGroupId - The ID of the filter group to which the pill belongs.
 * @param {setColumnFilters} setColumnFilters - A function to set column filters.
 * @returns {ReactElement} The rendered pill element.
 */
export const Pill = ({ filter, filterGroupId, setColumnFilters, label }: PillProps) => {
  const { setUnit, setValues, options } = useUnitAndRangeStore();
  // useCallback is used to memoize the onClick handler to avoid unnecessary re-renders
  const onClick = useCallback(
    () =>
      setColumnFilters((columnFilters) => {
        const position = columnFilters.findIndex((filter) => filter.id === filterGroupId);
        const filterId = columnFilters[position].id;
        const filterValue = columnFilters[position].value as string[];
        let elementValue = [] as string[];

        if (filterId !== 'capacityAndUnit') {
          elementValue = filterValue.filter((currentFilter) => {
            return currentFilter !== filter;
          });
        } else {
          const [label] = filter.split(':');
          if (label === text.filter_capacity) {
            setValues(undefined, undefined);
            elementValue = options;
          } else {
            setUnit(text.all);
          }
        }

        const element = {
          id: filterId,
          value: elementValue,
        };

        return replaceElementInArray(columnFilters, position, element);
      }),
    // Note: We don't need to track the changes on the unit and range values because they are going to be updated at this point
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setColumnFilters, filterGroupId, filter]
  );

  return (
    <Tag
      backgroundColor='hawkesBlue'
      color='tealishBlue'
      p={3}
      key={`${filter}-pill`}
      display='flex'
      gap={3}
      fontSize='s'
      fontWeight='normal'
      lineHeight='taller'
      onClick={onClick}
    >
      {label}
      <TagRightIcon
        as={SmallCloseIcon}
        bg='casper'
        m={-1.5}
        borderRadius='full'
        fontSize='l'
        _hover={{ cursor: 'pointer' }}
      />
    </Tag>
  );
};
